"use client";

import { useTranslations } from "next-intl";
import {
  CustomButton,
  FlexBetween,
  FlexBetweenCol,
  FlexCenterCol,
  FlexCol,
  TextField,
} from "../ui";
import Image from "next/image";
import {
  cn,
  formatNumber,
  formatPrice,
  generatePaymentCallbackUrl,
} from "@/lib";
import AppContainer from "../appContainer";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  IIncreaseWalletDepositPayload,
  ISelectableBaseProps,
  TCreditIncrementSchemaInfer,
} from "@/interfaces";
import { Controller, useForm } from "react-hook-form";
import ToggleCard from "../toggleCard";
import { useEffect, useRef, useState } from "react";
import { useGetWalletBalance, useIncreaseWalletDepositMutation } from "@/hooks";
import { creditIncrementSchema } from "@/validations";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useAppStore } from "@/store";
import { useSettings } from "@/hooks/useSettings";

const CUSTOM_AMOUNT_LABEL = "مبلغ دلخواه";

const basePrices: ISelectableBaseProps[] = [
  { label: "50000", isSelected: false },
  { label: "80000", isSelected: false },
  { label: "200000", isSelected: false },
  { label: CUSTOM_AMOUNT_LABEL, isSelected: false },
];

const formatPriceLabel = (label: string, thousandCurrency: string): string => {
  const thousands = Number(label.slice(0, -3));
  return !isNaN(thousands)
    ? `${thousands.toLocaleString()}${thousandCurrency}`
    : label;
};

const CreditIncrementSection = () => {
  const t = useTranslations();
  const { data: walletBalanceData, isLoading } = useGetWalletBalance();
  const pathname = usePathname();
  const { setPreviousPath } = useAppStore();
  const { data: session } = useSession();
  const [prices, setPrices] = useState<ISelectableBaseProps[]>(basePrices);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    isPending: isSubmitting,
    mutate: increaseWalletDepositFn,
    error: submittingError,
  } = useIncreaseWalletDepositMutation(session?.user.token);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors: formError },
    setValue,
  } = useForm<TCreditIncrementSchemaInfer>({
    resolver: zodResolver(creditIncrementSchema),
    defaultValues: {
      digit: "80000",
      bank: "",
    },
    mode: "onChange",
  });

  const { gateways: availableGateways, hasActiveGateways } = useSettings(
    session?.user.token,
  );
  const inputValue = watch("digit");

  const presetValues = basePrices
    .map((p) => p.label)
    .filter((l) => l !== CUSTOM_AMOUNT_LABEL);

  useEffect(() => {
    if (!inputValue) return;
    const isPreset = presetValues.includes(inputValue);
    setPrices((prev) => {
      const alreadyCorrect = prev.every((item) =>
        isPreset
          ? item.isSelected === (item.label === inputValue)
          : item.isSelected === (item.label === CUSTOM_AMOUNT_LABEL),
      );
      if (alreadyCorrect) return prev;
      return prev.map((item) => ({
        ...item,
        isSelected: isPreset
          ? item.label === inputValue
          : item.label === CUSTOM_AMOUNT_LABEL,
      }));
    });
  }, [inputValue]);

  const handleSelect = (index: number) => {
    const updated = prices.map((item, i) => {
      if (i === index) {
        const isCustom = item.label === CUSTOM_AMOUNT_LABEL;
        if (isCustom) {
          setValue("digit", "", { shouldValidate: false });
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        } else {
          setValue("digit", item.label, { shouldValidate: true });
        }
        return { ...item, isSelected: !item.isSelected };
      }
      return { ...item, isSelected: false };
    });
    setPrices(updated);
  };

  const [payments, setPayment] = useState<ISelectableBaseProps[]>([]);

  useEffect(() => {
    if (availableGateways.length > 0) {
      setPayment(availableGateways);
      let defaultPayment = availableGateways.find((i) => i.isSelected)?.key;
      if (!defaultPayment) {
        defaultPayment = availableGateways[0].key;
      }
      setValue("bank", defaultPayment, { shouldValidate: false });
    }
  }, [availableGateways, setValue]);

  const handleSelectPayment = (index: number) => {
    const updated = payments.map((item, i) => {
      if (i === index) {
        setValue("bank", item.key, { shouldValidate: false });
        return { ...item, isSelected: true };
      }
      return { ...item, isSelected: false };
    });
    setPayment(updated);
  };

  const onSubmit = (data: TCreditIncrementSchemaInfer) => {
    const payload: IIncreaseWalletDepositPayload = {
      amount: data.digit,
      callback_url: generatePaymentCallbackUrl(pathname),
    };

    increaseWalletDepositFn(
      { payload, bank: data.bank },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathname);
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };

  return (
    <section className="relative z-30 mt-[-116px] mb-14 xl:mb-20">
      <AppContainer className="h-auto">
        <div className="mx-auto size-full max-w-[700px] overflow-hidden rounded-xl bg-white p-8 shadow lg:max-w-[900px] xl:max-w-full">
          <FlexBetween gap={10} className="items-stretch lg:gap-20">
            <FlexCenterCol
              className={cn(
                "basis-full items-stretch sm:basis-8/12 lg:basis-1/2",
              )}
            >
              <FlexBetween gap={1} className="mb-4 w-max xl:mb-8">
                <p>{t("wallet.balance")} :</p>
                <p className="text-primary-main">
                  {!isLoading && (
                    <>
                      <span
                        dir="ltr"
                        className={cn(
                          Number(walletBalanceData?.data?.balance) < 0
                            ? "text-error-main"
                            : "text-primary-main",
                        )}
                      >
                        {formatNumber(walletBalanceData?.data?.balance || 0)}
                      </span>
                      <span
                        className={cn(
                          Number(walletBalanceData?.data?.balance) < 0
                            ? "text-error-main"
                            : "text-primary-main",
                        )}
                      >
                        {" " + t("wallet.currency")}
                      </span>
                    </>
                  )}
                </p>
              </FlexBetween>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FlexBetweenCol gap={8} className={cn("w-full")}>
                  <Controller
                    name={"digit"}
                    control={control}
                    render={({ field }) => (
                      <FlexCol className="w-full">
                        <TextField
                          {...field}
                          ref={inputRef}
                          onChange={(e) => {
                            field.onChange(e);
                          }}
                          aria-label="digit"
                          hasShadow
                          maxLength={9}
                          suffix="تومان"
                          numeric
                          inputMode="numeric"
                          placeholder={t("wallet.incrementPlaceholder")}
                          inputClassName={cn("dir-ltr h-10")}
                          error={formError.digit?.message}
                        />
                        {inputValue && (
                          <p className="text-success-main mt-3 px-4 text-sm">
                            {formatPrice(inputValue)}
                          </p>
                        )}
                      </FlexCol>
                    )}
                  />

                  <FlexBetween className="w-full gap-2 sm:gap-4">
                    {prices.map((item, idx) => (
                      <ToggleCard
                        id={`toggle-btn-${item.label}`}
                        className={cn(
                          "border-primary-main h-10 grow py-0 text-xs sm:text-sm xl:h-12 xl:text-base",
                          item.isSelected && "bg-primary-light",
                          !item.isSelected &&
                            "hover:text-secondary-main transition-colors",
                        )}
                        key={idx}
                        isSelected={item.isSelected}
                        setSelected={() => handleSelect(idx)}
                      >
                        <p>
                          {formatPriceLabel(
                            item.label,
                            t("wallet.thousandCurrency"),
                          )}
                        </p>
                      </ToggleCard>
                    ))}
                  </FlexBetween>
                  <FlexCol className="w-full gap-2">
                    <h3 className="text-right! text-base font-semibold">
                      پرداخت آنلاین
                    </h3>
                    <div className="grid w-full grid-cols-12 gap-4 sm:gap-6">
                      {payments.map((item, idx) => (
                        <ToggleCard
                          id={`toggle-btn-payment-${item.key}`}
                          className={cn(
                            "shadow-main col-span-12 h-10 grow py-0 text-xs sm:text-sm md:col-span-6 lg:col-span-6 xl:h-12 xl:text-base",
                            item.isSelected &&
                              "shadow-main! border-green-600 bg-green-200/20!",
                            !item.isSelected &&
                              "transition-colors hover:bg-green-200/20! hover:text-green-800",
                          )}
                          key={idx}
                          isSelected={item.isSelected}
                          setSelected={() => handleSelectPayment(idx)}
                        >
                          <div className="flex items-center">
                            <Image
                              className="w-[40px]"
                              src={item.icon || ""}
                              alt={item.label}
                            />
                            <p className={cn("mx-2")}>{item.label}</p>
                          </div>
                        </ToggleCard>
                      ))}
                    </div>
                  </FlexCol>

                  <CustomButton
                    id="wallet-credit-increment-submit"
                    isLoading={isSubmitting}
                    type="submit"
                    className="w-max xl:mt-4"
                  >
                    {t("wallet.incrementButton")}
                  </CustomButton>
                </FlexBetweenCol>
              </form>
            </FlexCenterCol>

            <figure className={cn("hidden basis-4/12 sm:block lg:basis-1/2")}>
              <Image
                src="/svg/saving-money-amico.svg"
                width={0}
                height={0}
                sizes="100vw"
                className={cn("size-full h-80 object-contain")}
                alt={t("wallet.imageAlt")}
              />
            </figure>
          </FlexBetween>
          <div className="mt-4 w-full max-w-[600px]">
            <p className="text-error-main text-justify text-sm">
              بر اساس سیاست‌های مالی سامانه و الزامات مرتبط با مقررات مبارزه با
              پولشویی،{" "}
              <b>حداقل مبلغ هر درخواست عودت به حساب بانکی ۵۰۰٬۰۰۰ تومان</b>{" "}
              تعیین شده است.
              <br />
              در صورتی که مبالغ کمتر از این حد نصاب،{" "}
              <b>به‌صورت مرحله‌ای یا عمدی</b> به ۵۰۰٬۰۰۰ تومان رسانده شوند،
              مشمول عودت نخواهند بود و امکان بازگشت وجه وجود ندارد.
              <br />
              خواهشمند است پیش از انجام پرداخت، مبلغ شارژ را با دقت انتخاب
              فرمایید. اعتبار کیف پول شما نزد سامانه نوین وان محفوظ بوده و در
              تمامی خدمات قابل استفاده می‌باشد.
            </p>
          </div>
        </div>
      </AppContainer>
    </section>
  );
};

export default CreditIncrementSection;
